import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [authError, setAuthError] = useState('');
  const [documents, setDocuments] = useState({ departments: [] });
  const [loading, setLoading] = useState(false);
  const [expandedDepartments, setExpandedDepartments] = useState({});
  const [showLogin, setShowLogin] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await fetch('/admin-api/auth.php?action=check');
      const data = await response.json();
      if (data.authenticated) {
        setIsAuthenticated(true);
        loadDocuments();
      }
    } catch (error) {
      console.error('Ошибка:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    
    const formData = new FormData();
    formData.append('action', 'login');
    formData.append('login', login);
    formData.append('password', password);
    
    try {
      const response = await fetch('/admin-api/auth.php', {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      
      if (data.success) {
        setIsVerifying(true);
        setShowLogin(false);
      } else {
        setAuthError(data.message);
      }
    } catch (error) {
      setAuthError('Ошибка соединения с сервером');
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setAuthError('');
    
    const formData = new FormData();
    formData.append('action', 'verify');
    formData.append('code', verificationCode);
    
    try {
      const response = await fetch('/admin-api/auth.php', {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      
      if (data.success) {
        setIsAuthenticated(true);
        setIsVerifying(false);
        loadDocuments();
      } else {
        setAuthError(data.message);
      }
    } catch (error) {
      setAuthError('Ошибка соединения с сервером');
    }
  };

  const handleLogout = async () => {
    const formData = new FormData();
    formData.append('action', 'logout');
    await fetch('/admin-api/auth.php', { method: 'POST', body: formData });
    setIsAuthenticated(false);
    setIsVerifying(false);
    setShowLogin(true);
    setLogin('');
    setPassword('');
    setVerificationCode('');
    setDocuments({ departments: [] });
  };

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const response = await fetch('/admin-api/get_documents.php');
      const data = await response.json();
      if (data.success) {
        setDocuments(data);
      }
    } catch (error) {
      console.error('Ошибка:', error);
    }
    setLoading(false);
  };

  const downloadFile = (department, patient, file) => {
    window.open(`/admin-api/download_file.php?department=${encodeURIComponent(department)}&patient=${encodeURIComponent(patient)}&file=${encodeURIComponent(file.name)}`, '_blank');
  };

  const toggleDepartment = (deptName) => {
    setExpandedDepartments(prev => ({
      ...prev,
      [deptName]: !prev[deptName]
    }));
  };

  // Страница авторизации
  if (!isAuthenticated) {
    return (
      <div className="admin-auth-container">
        <div className="admin-auth-card">
          <div className="admin-auth-header">
            <h2>🔐 Административная панель</h2>
            <p className="auth-subtitle">АИС «Наркодиспансер»</p>
            <Link to="/" className="back-link">← На главную</Link>
          </div>
          
          {showLogin && !isVerifying ? (
            <form onSubmit={handleLogin}>
              <div className="form-group">
                <label>Логин</label>
                <input 
                  type="text" 
                  value={login} 
                  onChange={(e) => setLogin(e.target.value)} 
                  placeholder="medzentr" 
                  required 
                />
              </div>
              <div className="form-group">
                <label>Пароль</label>
                <input 
                  type="password" 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  placeholder="••••••••" 
                  required 
                />
              </div>
              {authError && <div className="auth-error">{authError}</div>}
              <button type="submit" className="auth-button">Войти</button>
            </form>
          ) : (
            <form onSubmit={handleVerify}>
              <div className="form-group">
                <label>Код подтверждения</label>
                <input 
                  type="text" 
                  value={verificationCode} 
                  onChange={(e) => setVerificationCode(e.target.value)} 
                  placeholder="6 цифр" 
                  maxLength="6" 
                  required 
                />
              </div>
              <p className="auth-hint">📧 Код отправлен на почту mantulenkoa.v@sseu.ru</p>
              {authError && <div className="auth-error">{authError}</div>}
              <button type="submit" className="auth-button">Подтвердить</button>
              <button 
                type="button" 
                className="auth-button-secondary" 
                onClick={() => { 
                  setIsVerifying(false); 
                  setShowLogin(true);
                  setAuthError(''); 
                }}
              >
                Назад
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  // Основной интерфейс админ-панели
  return (
    <div className="admin-app">
      <header className="admin-header">
        <div className="header-content">
          <div className="header-left">
            <h1>🏥 Административная панель</h1>
            <Link to="/" className="back-link-white">← На главную</Link>
          </div>
          <div className="header-info">
            <span className="stats-badge">
              📁 {documents.departments?.reduce((acc, d) => acc + d.patients.length, 0)} пациентов
            </span>
            <button onClick={handleLogout} className="logout-button">Выйти</button>
          </div>
        </div>
        <p>Управление документами пациентов</p>
      </header>

      <main className="admin-main">
        <div className="toolbar">
          <button onClick={loadDocuments} className="refresh-button">🔄 Обновить</button>
          <div className="search-box">
            <input 
              type="text" 
              placeholder="Поиск по пациентам..." 
              onChange={(e) => {
                const searchTerm = e.target.value.toLowerCase();
                document.querySelectorAll('.patient-card').forEach(row => {
                  const text = row.textContent.toLowerCase();
                  row.style.display = text.includes(searchTerm) ? '' : 'none';
                });
              }}
            />
          </div>
        </div>

        {loading ? (
          <div className="loading">Загрузка документов...</div>
        ) : documents.departments?.length === 0 ? (
          <div className="no-documents">📭 Документов пока нет</div>
        ) : (
          <div className="departments-list">
            {documents.departments?.map((department, idx) => (
              <div key={idx} className="department-card">
                <div className="department-header" onClick={() => toggleDepartment(department.name)}>
                  <span className="department-icon">{expandedDepartments[department.name] ? '📂' : '📁'}</span>
                  <span className="department-name">{department.name.replace(/_/g, ' ')}</span>
                  <span className="patient-count">({department.patients.length})</span>
                  <span className="expand-icon">{expandedDepartments[department.name] ? '▼' : '▶'}</span>
                </div>
                
                {expandedDepartments[department.name] && (
                  <div className="patients-list">
                    {department.patients.map((patient, pIdx) => (
                      <div key={pIdx} className="patient-card">
                        <div className="patient-header">
                          <span className="patient-icon">👤</span>
                          <span className="patient-name">{patient.folder.replace(/_/g, ' ')}</span>
                        </div>
                        <div className="documents-grid">
                          {patient.files.map((file, fIdx) => {
                            let icon = '📄', label = 'Документ';
                            if (file.name.includes('Soglasie_PD')) { icon = '📄'; label = 'Согласие на обработку ПД'; }
                            else if (file.name.includes('Informirovannoe_soglasie')) { icon = '📋'; label = 'Информированное согласие'; }
                            else if (file.name.includes('Medicinskaya_karta')) { icon = '🏥'; label = 'Медицинская карта'; }
                            else if (file.name.includes('Dogovor')) { icon = '📑'; label = 'Договор'; }
                            
                            return (
                              <button key={fIdx} className="doc-button" onClick={() => downloadFile(department.name, patient.folder, file)}>
                                <span className="doc-icon">{icon}</span>
                                <span className="doc-label">{label}</span>
                                <span className="doc-size">{file.size_formatted}</span>
                                <span className="doc-date">{file.date}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default AdminPage;